<template>
  <div>
    <template v-if="attachments && attachments.length > 0">
      <template v-for="attachment in attachments" :key="attachment.id">
        <div v-if="isImage(attachment)" class="p-chat-item__attachment-image">
          <!-- Plain anchor, no JS: the view URL is already resolved by the
               time this renders (PChatItem pre-fetches it for images), so a
               real user click opens the full-size image. Routing it through
               window.open instead is what pop-up blockers ate. -->
          <a
            v-if="getAttachmentViewUrl(attachment.id)"
            :href="getAttachmentViewUrl(attachment.id)"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img :src="getAttachmentViewUrl(attachment.id)" :alt="attachment.fileName" />
          </a>
          <div v-else class="p-chat-item__attachment-loading">Loading...</div>
          <p-chat-timestamp
            :time="time"
            class="p-chat-item__image-time f-utility-10 t-fg-tertiary"
          />
        </div>
        <div v-else class="p-chat-item__attachment-file">
          <div class="p-chat-item__file-content">
            <div class="p-chat-item__file-icon">
              <p-icon icon="paper-note" size="md" color="fg-secondary" />
            </div>
            <div class="p-chat-item__file-info">
              <div class="f-body-14 t-fg-primary">{{ attachment.fileName }}</div>
              <div class="f-utility-12 t-fg-tertiary">
                {{ formatFileSize(attachment.sizeBytes ?? 0) }} •
                {{ getFileExtension(attachment.fileName) }}
              </div>
            </div>
            <!-- One control, always live. It used to render as a
                 `--disabled` anchor until a download URL was cached, but
                 nothing ever pre-fetched one for files and that modifier
                 sets `pointer-events: none` — so the click that would have
                 fetched it never fired and no file was ever downloadable. -->
            <div
              v-if="isAttachmentLoading(attachment.id)"
              class="p-chat-item__file-download p-chat-item__file-download--loading"
            >
              <p-loading size="sm" />
            </div>
            <button
              v-else
              type="button"
              class="p-chat-item__file-download"
              :aria-label="attachment.fileName"
              @click="handleDownloadClick(attachment)"
            >
              <p-icon icon="download" size="sm" color="fg-primary" />
            </button>
          </div>
          <p-chat-timestamp
            :time="time"
            class="p-chat-item__file-time f-utility-10 t-fg-tertiary"
          />
        </div>
      </template>
    </template>
    <div v-if="message" class="p-chat-item__text">
      <template v-for="(part, i) in messageParts" :key="i">
        <a v-if="part.href" :href="part.href" target="_blank" rel="noopener noreferrer">{{
          part.text
        }}</a>
        <template v-else>{{ part.text }}</template>
      </template>
    </div>
    <!-- Saat, ek varsa her ek satırında basılıyor (mevcut davranış). Düzenlendi
         işareti ise MESAJ düzeyinde: `v-for` içinde bırakılınca üç ekli bir
         mesajda üç kez tekrarlıyor ve tekrar eden saat "dosya başına bilgi"
         gibi okunurken tekrar eden "düzenlendi" üç ayrı düzenleme gibi
         okunuyordu. `editedAt` mesaja ait, eke değil. -->
    <p-chat-timestamp
      :time="hasAttachments ? undefined : time"
      :edited="edited"
      class="p-chat-item__time f-utility-10 t-fg-tertiary"
    />
  </div>
</template>

<script setup lang="ts">
interface Attachment {
  id: string | number
  type: 'image' | 'file'
  uri?: string
  fileName: string
  mimeType: string
  sizeBytes?: number
}

interface Props {
  attachments?: Attachment[]
  message?: string
  time?: string
  // True once an agent has edited this message. The customer has no edit
  // affordance of their own — this is purely a marker on what an agent did.
  edited?: boolean
  getAttachmentViewUrl: (_id: string | number) => string
  isAttachmentLoading: (_id: string | number) => boolean
  handleDownloadClick: (_attachment: Attachment) => void
  isImage: (_attachment: Attachment) => boolean
  formatFileSize: (_bytes: number) => string
  getFileExtension: (_fileName: string) => string
}

const props = defineProps<Props>()

const hasAttachments = computed(() => (props.attachments?.length ?? 0) > 0)

// A word is a link if it starts with http(s):// or www. (www. gets an https
// prefix for the href). Returns undefined for non-links.
function toHref(word: string): string | undefined {
  const safe = word || ''
  if (!/^(https?:\/\/|www\.)/i.test(safe)) return undefined
  // Case-insensitive protocol check — a `www.` word (or any non-http prefix)
  // gets an https:// prefix; an existing http(s):// (any casing) is kept as-is.
  return /^https?:\/\//i.test(safe) ? safe : `https://${safe}`
}

// Split the message into whitespace-separated words (whitespace kept) so URL
// words render as links and the rest stays plain text ({{ }} auto-escapes,
// so no v-html / manual escaping needed).
const messageParts = computed(() => {
  return (props.message ?? '').split(/(\s+)/).map((word) => ({ text: word, href: toHref(word) }))
})
</script>
